"use client";

import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import {
    Field,
    FieldLabel,
    FieldError,
} from "../../@/components/ui/field";

const editPathwayTypeSchema = z.object({
    name: z.string().min(1, "Title is required"),
    description: z.string().optional(),
});

export default function EditPathwayTypeDialog({ open, onOpenChange, pathwayType, onUpdated }) {
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(editPathwayTypeSchema),
        defaultValues: {
            name: pathwayType?.type || "",
            description: pathwayType?.description || "",
        },
    });

    // Update form values when pathwayType changes
    useEffect(() => {
        form.reset({
            name: pathwayType?.type || "",
            description: pathwayType?.description || "",
        });
    }, [pathwayType, form]);

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const token = await getToken();
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            };

            const payload = {
                name: data.name,
                description: data.description,
            };

            await axios.put(
                `${api_endpoint}/pathway/levels/update/${pathwayType.id}`,
                payload,
                reqConfig
            );
            toast.success("Pathway type updated successfully");
            onOpenChange(false);
            if (onUpdated) onUpdated();
        } catch (error) {
            console.error("Error updating pathway type:", error);
            toast.error("Failed to update pathway type. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.reset();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">
                        Edit Pathway Type
                    </DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Update the pathway type details below.
                    </DialogDescription>
                </DialogHeader>
                <form
                    id="edit-pathway-type-form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-6"
                >
                    <Field>
                        <FieldLabel required htmlFor="name">Title</FieldLabel>
                        <Input
                            id="name"
                            placeholder="Enter pathway type title"
                            disabled={loading}
                            {...form.register("name")}
                        />
                        <FieldError>
                            {form.formState.errors.name?.message}
                        </FieldError>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="description">Description</FieldLabel>
                        <Textarea
                            id="description"
                            placeholder="Enter pathway type description"
                            rows={4}
                            className="resize-none"
                            disabled={loading}
                            {...form.register("description")}
                        />
                        <FieldError>
                            {form.formState.errors.description?.message}
                        </FieldError>
                    </Field>
                </form>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="font-body"
                    >
                        Cancel
                    </Button>
                    <Button
                        form="edit-pathway-type-form"
                        type="submit"
                        variant=""
                        disabled={loading}
                        className=""
                    >
                        {loading ? <ClipLoader size={20} color="#ffffff" /> : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}